import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  LineSeries,
  DateTime,
  Tooltip as ChartTooltip,
} from '@syncfusion/ej2-react-charts';

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '0.5px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SecLabel = ({ children, sx = {} }) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
const WEEK_INTERVAL_STEPS = [1, 2, 4, 8, 12, 26];

/** Pick a week interval that keeps ~12–16 x-axis labels without affecting tooltips. */
const getXAxisWeekInterval = (...dataSeries) => {
  const times = dataSeries
    .flatMap((series) => series || [])
    .map((d) => new Date(d.x).getTime())
    .filter((t) => !isNaN(t));
  if (times.length < 2) return 2;

  const spanWeeks = (Math.max(...times) - Math.min(...times)) / MS_PER_WEEK;
  const target = Math.ceil(spanWeeks / 14);
  return WEEK_INTERVAL_STEPS.find((step) => step >= target) ?? 26;
};

/**
 * Reusable Cumulative Hours chart — Syncfusion 4 LineSeries.
 *
 * Props:
 *   title       — chart heading
 *   subtitle    — muted caption under the title (work-package scope)
 *   budgetHrs   — static number for the flat ceiling line
 *   plannedData — [{ x: Date, y: number }]  starts at 0 -> full project duration
 *   earnedData  — [{ x: Date, y: number|null }]  stops at today
 *   actualData  — [{ x: Date, y: number|null }]  stops at today
 */
const DEFAULT_SUBTITLE =
  'Work packages only (excludes general conditions & change orders)';

const CumChart = ({
  title,
  subtitle = DEFAULT_SUBTITLE,
  budgetHrs,
  plannedData,
  earnedData,
  actualData,
  height = '210px',
  onExpand,
  expandAriaLabel,
}) => {
  // One point per planned week so Budget can lead the shared tooltip
  // without stealing the header date from a two-point start/end line.
  const budgetLineData = (plannedData || []).map((d) => ({
    x: d.x,
    y: budgetHrs,
  }));

  // Filter out null/undefined y-values so the tooltip doesn't anchor to stale points
  const filteredEarned = (earnedData || []).filter((d) => d.y != null);
  const filteredActual = (actualData || []).filter((d) => d.y != null);
  const xAxisWeekInterval = getXAxisWeekInterval(
    plannedData,
    earnedData,
    actualData
  );

  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <SecLabel sx={{ mb: 0 }}>{title}</SecLabel>
          {subtitle && (
            <Typography
              sx={{
                mt: 0.25,
                fontSize: '9px',
                fontWeight: 400,
                letterSpacing: 0,
                textTransform: 'none',
                color: 'text.secondary',
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {onExpand && (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onExpand();
            }}
            title="Expand chart"
            aria-label={expandAriaLabel || `Expand ${title}`}
            sx={{ mt: -0.75, mr: -0.75 }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Custom legend (Syncfusion legend hidden) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 16,
              height: 2,
              bgcolor: '#D3D1C7',
              borderTop: '2px dashed #D3D1C7',
            }}
          />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            Budget
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 16,
              height: 2,
              borderTop: '2px dashed #378ADD',
            }}
          />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            Planned
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 2, bgcolor: '#1D9E75' }} />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            Earned
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 2, bgcolor: '#E6813D' }} />
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            Actual
          </Typography>
        </Box>
      </Box>

      <ChartComponent
        height={height}
        primaryXAxis={{
          valueType: 'DateTime',
          labelFormat: 'MMM d',
          intervalType: 'Weeks',
          interval: xAxisWeekInterval,
          rangePadding: 'Round',
          edgeLabelPlacement: 'Shift',
          majorGridLines: { width: 0 },
          labelStyle: { size: '9px' },
        }}
        primaryYAxis={{
          majorGridLines: { color: 'rgba(136,135,128,.12)' },
          labelStyle: { size: '9px' },
          labelFormat: '{value}',
        }}
        axisLabelRender={(args) => {
          if (args.axis.name === 'primaryXAxis') {
            const d = new Date(args.value);
            if (d.getMonth() === 0) {
              args.text = `Jan '${String(d.getFullYear()).slice(-2)}`;
            }
          }
        }}
        tooltipRender={(args) => {
          if (args.headerText) {
            const d = new Date(args.headerText);
            if (!isNaN(d)) {
              args.headerText = d.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
            }
          }
        }}
        tooltip={{
          enable: true,
          shared: true,
          format: '${series.name} : <b>${point.y}</b>',
          fadeOutDuration: 200,
        }}
        legendSettings={{ visible: false }}
        chartArea={{ border: { width: 0 } }}
      >
        <Inject services={[LineSeries, DateTime, ChartTooltip]} />
        <SeriesCollectionDirective>
          {/* Tooltip order follows series order. Budget first, then Planned,
              Earned, Actual. Budget uses a point per planned week so the
              shared tooltip header still gets a real week date. */}
          <SeriesDirective
            dataSource={budgetLineData}
            xName="x"
            yName="y"
            name="Budget"
            type="Line"
            fill="#D3D1C7"
            width={1}
            dashArray="7,4"
          />
          <SeriesDirective
            dataSource={plannedData}
            xName="x"
            yName="y"
            name="Planned"
            type="Line"
            fill="#378ADD"
            width={2}
            dashArray="7,4"
          />
          <SeriesDirective
            dataSource={filteredEarned}
            xName="x"
            yName="y"
            name="Earned"
            type="Line"
            fill="#1D9E75"
            width={2}
          />
          <SeriesDirective
            dataSource={filteredActual}
            xName="x"
            yName="y"
            name="Actual"
            type="Line"
            fill="#E6813D"
            width={2}
          />
        </SeriesCollectionDirective>
      </ChartComponent>
    </Card>
  );
};

export default CumChart;
